import { Suspense, useEffect, useRef, useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BarChart3, CloudOff, MessagesSquare, Route as RouteIcon, UserRound } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { connectRealtime, disconnectRealtime, onLoadChanged, onReconnect, onShiftFinished, useChatStore } from '@/features/chat/socket';
import { useViewportBox } from '@/features/chat/lib/useViewportBox';
import { syncPush } from '@/features/notifications/push';
import { flushQueue, setQueueSyncedHandler, useQueue } from '@/lib/queue';
import { SHIFT_KEY, useActiveShift } from '@/lib/shift-cache';
import { useTrackRecorder } from '@/lib/track';
import { useScreenWakeLock } from '@/lib/wakelock';
import { Spinner } from '@/components/ui';

function subscribeOnline(cb: () => void) {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine);
}

const TABS = [
  { to: '/', label: 'Маршрут', icon: RouteIcon },
  { to: '/chat', label: 'Чат', icon: MessagesSquare },
  { to: '/stats', label: 'Аналитика', icon: BarChart3 },
  { to: '/profile', label: 'Профиль', icon: UserRound },
] as const;

export default function Layout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const unread = useChatStore((s) => s.unread);
  const queued = useQueue((s) => s.items.length);
  const online = useOnline();
  // Смена идёт — пишем GPS-трек (каждая точка помнит свою смену) и не даём экрану погаснуть.
  const shiftId = useActiveShift().data?.shift.id ?? null;
  useTrackRecorder(shiftId);
  useScreenWakeLock(!!shiftId);

  const chatScreen = location.pathname === '/chat';
  // Экран точки — свой нижний блок с суммой и кнопкой, панель вкладок там мешает.
  const pointScreen = location.pathname.startsWith('/point/');
  const shellRef = useRef<HTMLDivElement>(null);
  const keyboardOpen = useViewportBox(shellRef, chatScreen);

  useEffect(() => {
    connectRealtime();
    void syncPush();
    return () => disconnectRealtime();
  }, []);

  // Очередь: отправляем при появлении сети, возврате в приложение, переподключении сокета.
  useEffect(() => {
    setQueueSyncedHandler(() => {
      void qc.invalidateQueries({ queryKey: SHIFT_KEY });
      void qc.invalidateQueries({ queryKey: ['point'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    });
    const tryFlush = () => void flushQueue();
    tryFlush();
    const onVisible = () => document.visibilityState === 'visible' && tryFlush();
    window.addEventListener('online', tryFlush);
    document.addEventListener('visibilitychange', onVisible);
    const offReconnect = onReconnect(() => {
      tryFlush();
      void qc.invalidateQueries({ queryKey: SHIFT_KEY });
    });
    return () => {
      window.removeEventListener('online', tryFlush);
      document.removeEventListener('visibilitychange', onVisible);
      offReconnect();
    };
  }, [qc]);

  useEffect(() => {
    if (queued === 0) return;
    const t = window.setInterval(() => void flushQueue(), 20_000);
    return () => window.clearInterval(t);
  }, [queued]);

  useEffect(
    () =>
      onShiftFinished((p) => {
        toast(p.by === 'owner' ? 'Руководитель завершил вашу смену' : 'Смена завершена автоматически', { duration: 6000 });
        void qc.invalidateQueries({ queryKey: SHIFT_KEY });
        navigate('/');
      }),
    [qc, navigate],
  );

  useEffect(() => onLoadChanged(() => void qc.invalidateQueries({ queryKey: ['loads'] })), [qc]);

  // Нажали на пуш при открытом приложении — переходим без перезагрузки.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'open-url' && e.data.url?.startsWith('/')) navigate(e.data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const showTabs = !pointScreen && !keyboardOpen;

  return (
    <div
      ref={shellRef}
      className={clsx('flex flex-col bg-paper', chatScreen ? 'fixed inset-x-0 top-0 h-dvh overflow-hidden overscroll-none' : 'min-h-dvh')}
    >
      {!online && (
        <div className="pt-safe z-30 bg-ink text-white">
          <div className="mx-auto flex max-w-md items-center gap-2 px-4 py-2 text-[13px]">
            <CloudOff size={15} className="shrink-0" />
            <span>Нет сети — всё сохраним и отправим, как появится связь</span>
          </div>
        </div>
      )}
      <main className={clsx('flex min-h-0 flex-1 flex-col', showTabs && 'pb-tabbar')}>
        {/* Ленивые экраны (чат, аналитика, профиль) — панель вкладок остаётся на месте. */}
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </main>
      {showTabs && (
        <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
          <div className="mx-auto grid h-16 max-w-md grid-cols-4">
            {TABS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  clsx('relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium', isActive ? 'text-orange' : 'text-ink/50')
                }
              >
                <span className="relative">
                  <Icon size={22} strokeWidth={1.9} />
                  {to === '/chat' && unread > 0 && (
                    <span className="absolute -right-2.5 -top-1.5 min-w-[18px] rounded-full bg-orange px-1 text-center text-[10px] font-semibold leading-[18px] text-white tabular-nums ring-2 ring-white">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                  {to === '/' && queued > 0 && (
                    <span className="absolute -right-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" aria-label="Есть неотправленные точки" />
                  )}
                </span>
                {label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
